export type PdfZoomMode = "fit-page" | "fit-width" | "percent";

export type PdfZoomState = {
  mode: PdfZoomMode;
  /** Used when mode is `percent`; 100 = fit to width baseline. */
  percent: number;
};

export const getFitScales = (
  pageWidth: number,
  pageHeight: number,
  viewerWidth: number,
  viewerHeight: number
) => {
  const hasUsableViewerWidth = viewerWidth > 50;
  const hasUsableViewerHeight = viewerHeight > 50;
  const availableWidth = hasUsableViewerWidth ? Math.max(viewerWidth - 12, 1) : 0;
  const availableHeight = hasUsableViewerHeight ? Math.max(viewerHeight - 24, 1) : 0;
  const fitWidthScale = hasUsableViewerWidth
    ? availableWidth / Math.max(pageWidth, 1)
    : 1;
  const fitPageScale =
    hasUsableViewerWidth && hasUsableViewerHeight
      ? Math.min(fitWidthScale, availableHeight / Math.max(pageHeight, 1))
      : fitWidthScale;
  return { fitWidthScale, fitPageScale };
};

const resolveEffectiveScale = (
  zoom: PdfZoomState,
  fitWidthScale: number,
  fitPageScale: number
) => {
  if (zoom.mode === "fit-page") return fitPageScale;
  if (zoom.mode === "fit-width") return fitWidthScale;
  return fitWidthScale * (zoom.percent / 100);
};

export type PageViewportParams = {
  effectiveRotation: number;
  effectiveScale: number;
  pixelRatio: number;
  cssWidth: number;
  cssHeight: number;
};

export const getPageViewportParams = (
  page: any,
  zoom: PdfZoomState,
  viewerWidth: number,
  viewerHeight: number,
  currentAngle: number
): PageViewportParams => {
  const baseRotation = Number(page.rotate || 0);
  const effectiveRotation = (baseRotation + currentAngle) % 360;
  const baseViewport = page.getViewport({
    scale: 1,
    rotation: effectiveRotation
  });
  const { fitWidthScale, fitPageScale } = getFitScales(
    baseViewport.width,
    baseViewport.height,
    viewerWidth,
    viewerHeight
  );

  const effectiveScale = resolveEffectiveScale(zoom, fitWidthScale, fitPageScale);

  const pixelRatio =
    globalThis.window === undefined
      ? 1
      : Math.min(globalThis.window.devicePixelRatio || 1, 3);
  const viewport = page.getViewport({
    scale: effectiveScale * pixelRatio,
    rotation: effectiveRotation
  });
  return {
    effectiveRotation,
    effectiveScale,
    pixelRatio,
    cssWidth: viewport.width / pixelRatio,
    cssHeight: viewport.height / pixelRatio
  };
};

export const getPageViewport = (
  page: any,
  zoom: PdfZoomState,
  viewerWidth: number,
  viewerHeight: number,
  currentAngle: number
) => {
  const params = getPageViewportParams(page, zoom, viewerWidth, viewerHeight, currentAngle);
  const baseRotation = Number(page.rotate || 0);
  const effectiveRotation = (baseRotation + currentAngle) % 360;
  return page.getViewport({
    scale: params.effectiveScale * params.pixelRatio,
    rotation: effectiveRotation
  });
};
